import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Chip,
  FormHelperText,
  Paper,
  Grid,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { parsePdbStructure, PdbChainInfo, buildContigString, parseContigString } from '@/utils/pdbUtils';

interface ContigSelectorProps {
  pdbContent: string | null;
  onChange: (value: string) => void;
  value: string;
  isBinderMode?: boolean;
}

interface ChainSegment {
  chainId: string;
  start: number;
  end: number;
}

const ContigSelector: React.FC<ContigSelectorProps> = ({
  pdbContent,
  onChange,
  value,
  isBinderMode = false
}) => {
  const [pdbInfo, setPdbInfo] = useState<PdbChainInfo[]>([]);
  const [selectedChain, setSelectedChain] = useState<string>('');
  const [residueRange, setResidueRange] = useState<number[]>([1, 100]);
  const [selections, setSelections] = useState<ChainSegment[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Parse PDB file when content changes
  useEffect(() => {
    console.log('ContigSelector: pdbContent changed', 
      pdbContent ? `PDB content available (${pdbContent.length} chars)` : 'No PDB content');
    
    if (pdbContent) {
      // Basic validation check for PDB content
      const hasPdbHeader = pdbContent.includes('HEADER') || 
                           pdbContent.includes('ATOM  ') || 
                           pdbContent.includes('HETATM');
      
      if (!hasPdbHeader) {
        console.error('ContigSelector: Invalid PDB content - no HEADER/ATOM/HETATM records found');
        setError('The uploaded file does not appear to be a valid PDB file. Please check the file format.');
        setPdbInfo([]);
        return;
      }
      
      try {
        const parsedInfo = parsePdbStructure(pdbContent);
        console.log('ContigSelector: parsed PDB structure', parsedInfo);
        
        if (parsedInfo.length > 0) {
          setPdbInfo(parsedInfo);
          // Set the first chain as default selection
          setSelectedChain(parsedInfo[0].chainId);
          setResidueRange([
            parsedInfo[0].residueRange.start,
            parsedInfo[0].residueRange.end
          ]);
          setError(null);
        } else {
          setError('No valid chains found in the PDB file. Please check that the file contains ATOM or HETATM records.');
          setPdbInfo([]);
        }
      } catch (err) {
        console.error('Error parsing PDB:', err);
        setError('Failed to parse PDB structure. Please ensure the file is in standard PDB format.');
        setPdbInfo([]);
      }
    } else {
      // Reset state when no PDB content is available
      setPdbInfo([]);
      setSelectedChain('');
      setResidueRange([1, 100]);
      setError(null);
    }
  }, [pdbContent]);

  // Parse the existing value when component mounts or when value changes externally
  useEffect(() => {
    if (value && pdbInfo.length > 0) {
      try {
        const parsedSegments = parseContigString(value);
        setSelections(parsedSegments);
      } catch (err) {
        console.error('Error parsing contig string:', err);
      }
    }
  }, [value, pdbInfo.length]);

  // Generate and update contig string when selections change
  useEffect(() => {
    if (selections.length === 0) return;
    
    try {
      let contigString;
      if (isBinderMode) {
        // In binder mode, we only use the first selection as the target
        // Format is [A1-50/0 100-100]
        const targetSegment = selections[0];
        contigString = `[${targetSegment.chainId}${targetSegment.start}-${targetSegment.end}/0 100-100]`;
      } else {
        // For normal mode, use the standard format without brackets
        contigString = buildContigString(selections);
      }
      
      console.log('Generated contig string:', contigString);
      
      // Only trigger onChange if the value has actually changed
      if (contigString !== value) {
        onChange(contigString);
      }
      
      // Clear any error related to contig format
      if (error && error.includes('contig')) {
        setError(null);
      }
    } catch (err) {
      console.error('Error generating contig string:', err);
      setError('Failed to generate a valid contig string');
    }
  }, [selections, isBinderMode, onChange, value, error]);

  // Update the selected chain and its available residue range
  const handleChainChange = (event: SelectChangeEvent) => {
    const chainId = event.target.value;
    setSelectedChain(chainId);
    
    const chainInfo = pdbInfo.find(info => info.chainId === chainId);
    if (chainInfo) {
      setResidueRange([chainInfo.residueRange.start, chainInfo.residueRange.end]);
    }
  };

  // Handle range slider changes
  const handleRangeChange = (_event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setResidueRange(newValue);
    }
  };

  // Add current selection to the list
  const handleAddSelection = () => {
    if (!selectedChain) return;
    
    const newSegment: ChainSegment = {
      chainId: selectedChain,
      start: residueRange[0],
      end: residueRange[1]
    };
    
    // In binder mode, we replace the existing selection
    if (isBinderMode) {
      setSelections([newSegment]);
    } else {
      // Check for overlapping selections
      const isOverlapping = selections.some(segment => 
        segment.chainId === selectedChain &&
        ((segment.start <= newSegment.end && segment.end >= newSegment.start) ||
         (newSegment.start <= segment.end && newSegment.end >= segment.start))
      );
      
      if (isOverlapping) {
        setError('Overlapping chain segments are not allowed');
        return;
      }
      
      setSelections([...selections, newSegment]);
    }
    
    setError(null);
  };

  // Remove a selection
  const handleRemoveSelection = (index: number) => {
    const newSelections = [...selections];
    newSelections.splice(index, 1);
    setSelections(newSelections);
  };

  // Format for display
  const formatSelection = (segment: ChainSegment) => {
    return `Chain ${segment.chainId}: ${segment.start}-${segment.end}`;
  };

  // Format contig string for display
  const displayContigString = () => {
    if (!value) return 'No contigs defined';
    
    if (isBinderMode) {
      // For binder mode, extract the target segment from the full contig string
      const match = value.match(/\[(.*?)\/0/);
      const targetSegment = match ? match[1] : 'Not set';
      return `Target: ${targetSegment} (Will create a binder protein to this region)`;
    } else {
      return `Contig: ${value}`;
    }
  };

  return (
    <Box>
      {!pdbContent ? (
        <Alert severity="info">
          Upload a PDB file to select chains and residue ranges
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {isBinderMode ? 'Select Binding Target Region' : 'Define Contig Segments'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="chain-select-label">Chain</InputLabel>
                <Select
                  labelId="chain-select-label"
                  value={selectedChain}
                  label="Chain"
                  onChange={handleChainChange}
                  disabled={pdbInfo.length === 0}
                >
                  {pdbInfo.map(chain => (
                    <MenuItem key={chain.chainId} value={chain.chainId}>
                      Chain {chain.chainId} ({chain.residueRange.start}-{chain.residueRange.end})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom variant="body2">
                Residue Range: {residueRange[0]} - {residueRange[1]}
              </Typography>
              <Slider
                value={residueRange}
                onChange={handleRangeChange}
                valueLabelDisplay="auto"
                min={pdbInfo.find(c => c.chainId === selectedChain)?.residueRange.start || 1}
                max={pdbInfo.find(c => c.chainId === selectedChain)?.residueRange.end || 100}
                disabled={!selectedChain}
                marks={[
                  { value: pdbInfo.find(c => c.chainId === selectedChain)?.residueRange.start || 1, label: '' },
                  { value: pdbInfo.find(c => c.chainId === selectedChain)?.residueRange.end || 100, label: '' }
                ]}
              />
            </Grid>
            
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddSelection}
                disabled={!selectedChain}
                fullWidth
              >
                {isBinderMode ? 'Set Target' : 'Add'}
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {isBinderMode ? 'Selected Target' : 'Selected Segments'}
            </Typography>
            
            {selections.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {isBinderMode 
                  ? 'No target region selected' 
                  : 'No segments selected yet'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selections.map((segment, index) => (
                  <Chip
                    key={`${segment.chainId}-${segment.start}-${segment.end}`}
                    label={formatSelection(segment)}
                    onDelete={() => handleRemoveSelection(index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
          
          {value && (
            <Box sx={{ mt: 3, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" fontFamily="monospace">
                {displayContigString()}
              </Typography>
            </Box>
          )}
          
          <FormHelperText>
            {isBinderMode 
              ? "Select the region of your protein that should be targeted for binding. The system will create a format like [A19-45/0 100-100] where A19-45 is your selected target and 100-100 is the binder length." 
              : "Select chain segments to include in your protein design. Format will be 'A19-71' or multiple segments like 'A19-71 B15-45'."}
          </FormHelperText>
          
          {isBinderMode && selections.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              RFDiffusion will design a binder protein that targets the selected region
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ContigSelector; 